import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { recallFootwearSizeRegion } from '@theme/variant-picker';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);

    this.#syncFootwearSizeRegion();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    // Custom suit: hidden add-on child lines are folded into the parent card, so
    // keep their quantities in step with the parent. Each child row carries
    // `data-parent-key` (the parent's key) and `data-parent-multiplier` (units per
    // parent); setting child qty to `quantity * multiplier` also clears the add-ons
    // when the parent is removed (quantity 0). Shopify does not scale them for us.
    const parentRow = this.refs.cartItemRows[line - 1];
    const parentKey = parentRow?.dataset.key;
    /** @type {Record<string, number>} */
    const childUpdates = {};
    if (parentKey) {
      for (const row of this.refs.cartItemRows) {
        if (row.dataset.parentKey !== parentKey || !row.dataset.key) continue;
        const multiplier = parseInt(row.dataset.parentMultiplier || '1', 10) || 1;
        childUpdates[row.dataset.key] = quantity * multiplier;
      }
    }
    const childUpdatePromise = Object.keys(childUpdates).length
      ? fetch(
          `${Theme.routes.cart_update_url}`,
          fetchConfig('json', { body: JSON.stringify({ updates: childUpdates }) }),
        )
      : Promise.resolve();

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    childUpdatePromise
      .then(() => fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body })))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html',
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          }),
        );

        this.#renderCartDrawer(parsedResponseText.sections[this.sectionId]);

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer
        .getSectionHTML(this.sectionId, false)
        .then((html) => this.#renderCartDrawer(html))
        .catch((error) => console.error(error));
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      this.#renderCartDrawer(cartItemsHtml);

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer
        .getSectionHTML(this.sectionId, false)
        .then((html) => this.#renderCartDrawer(html))
        .catch((error) => console.error(error));
    }
  };

  /**
   * Re-render the cart in place WITHOUT touching the cart drawer's `<dialog>`.
   *
   * The open drawer is a modal `<dialog>` living in the browser's top layer.
   * Morphing the whole header section reconciles the dialog subtree, which evicts
   * it from the top layer — it silently becomes a non-modal dialog, collapsing its
   * height and dropping it below the header (a transformed ancestor then captures
   * its `position: fixed`). So we morph only what actually changes on a cart
   * update: this component's content (it lives *inside* the dialog) and the header
   * cart-count bubble (`<cart-icon>`, *outside* it) — never the `<dialog>`, so it
   * stays `:modal`.
   *
   * @param {string} html - The re-rendered section HTML.
   */
  #renderCartDrawer(html) {
    const fragment = new DOMParser().parseFromString(html, 'text/html');

    const newContent =
      fragment.querySelector(`cart-items-component[data-section-id="${this.sectionId}"]`) ??
      fragment.querySelector('cart-items-component');

    // Unexpected structure — fall back to the full section morph.
    if (!newContent) {
      morphSection(this.sectionId, html);
      this.#syncFootwearSizeRegion();
      return;
    }

    // Cart content (inside the dialog): morph this component's children only.
    morph(this, newContent);
    this.#syncFootwearSizeRegion();

    // Reflect empty/non-empty state on the dialog. Toggling a class doesn't evict
    // it from the top layer (only DOM removal/move does), so this stays modal-safe.
    const dialog = this.closest('.cart-drawer__dialog');
    const newDialog = fragment.querySelector('.cart-drawer__dialog');
    if (dialog && newDialog) {
      dialog.classList.toggle('cart-drawer--empty', newDialog.classList.contains('cart-drawer--empty'));
    }

    // Cart-count bubble (outside the dialog): keep it in sync without re-rendering
    // — and thereby evicting — the dialog.
    const oldIcon = document.querySelector('cart-icon');
    const newIcon = fragment.querySelector('cart-icon');
    if (oldIcon && newIcon) morph(oldIcon, newIcon);
  }

  /**
   * Cart rows render with the market-based EU/UK/US size region by default (Liquid
   * has no access to the shopper's sessionStorage). If the shopper switched regions
   * on a PDP this session, override each row to match — same source of truth the
   * sticky add-to-cart bar re-syncs from after its own re-renders.
   */
  #syncFootwearSizeRegion() {
    const region = recallFootwearSizeRegion();
    if (!region) return;

    const normalizedRegion = region.toLowerCase();

    for (const row of this.querySelectorAll('[data-current-region]')) {
      if (!(row instanceof HTMLElement)) continue;
      row.dataset.currentRegion = normalizedRegion;

      for (const label of row.querySelectorAll('[data-region-label]')) {
        if (label instanceof HTMLElement) {
          label.hidden = label.dataset.regionLabel?.toLowerCase() !== normalizedRegion;
        }
      }
    }
  }

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
